/**
 * Single source of truth for the language each market is published in.
 *
 * Every value here was verified against the actual content served by the CMS
 * (https://d7n4bh3bohn60.cloudfront.net/assets/<iso>/text_content/prod/content.js).
 * Do NOT infer the language from the country code: several markets are published
 * in English (MY, KE, MM, BD, IN…) and UZ is published in Cyrillic script.
 *
 *   htmlLang — value for <html lang>, describes the language of the document.
 *   hreflang — value for <link rel="alternate" hreflang>, language + region.
 *
 * Note on region subtags: hreflang regions must be ISO 3166-1 alpha-2.
 * The UK market lives at /uk but its region subtag is GB (en-UK is invalid and
 * Google silently drops it).
 */

// iso path segment -> [htmlLang, hreflang]
const MARKET_LOCALES = {
    // Global hub — English, also the x-default target
    gl: ['en', 'en'],

    // English-speaking markets
    au: ['en', 'en-AU'],
    bd: ['en', 'en-BD'],
    ca: ['en', 'en-CA'],
    gh: ['en', 'en-GH'],
    in: ['en', 'en-IN'],
    ke: ['en', 'en-KE'],
    mm: ['en', 'en-MM'],
    my: ['en', 'en-MY'],   // content is English, not Malay
    ng: ['en', 'en-NG'],
    nz: ['en', 'en-NZ'],
    ug: ['en', 'en-UG'],
    uk: ['en', 'en-GB'],   // path is /uk, region subtag is GB
    us: ['en', 'en-US'],
    zm: ['en', 'en-ZM'],

    // Spanish-speaking markets
    ar: ['es', 'es-AR'],
    bo: ['es', 'es-BO'],
    co: ['es', 'es-CO'],
    cr: ['es', 'es-CR'],
    do: ['es', 'es-DO'],
    ec: ['es', 'es-EC'],
    es: ['es', 'es-ES'],
    gt: ['es', 'es-GT'],
    hn: ['es', 'es-HN'],
    mx: ['es', 'es-MX'],
    ni: ['es', 'es-NI'],
    pa: ['es', 'es-PA'],
    pe: ['es', 'es-PE'],
    py: ['es', 'es-PY'],
    sv: ['es', 'es-SV'],
    uy: ['es', 'es-UY'],

    // Portuguese-speaking markets
    ao: ['pt', 'pt-AO'],
    br: ['pt-BR', 'pt-BR'],
    pt: ['pt-PT', 'pt-PT'],

    // Everything else
    eg: ['ar', 'ar-EG'],
    gr: ['el', 'el-GR'],
    id: ['id', 'id-ID'],
    it: ['it', 'it-IT'],
    kh: ['km', 'km-KH'],
    kr: ['ko', 'ko-KR'],
    kz: ['ru', 'ru-KZ'],          // Kazakhstan is served in Russian
    pl: ['pl', 'pl-PL'],
    ro: ['ro', 'ro-RO'],
    ru: ['ru', 'ru-RU'],
    th: ['th', 'th-TH'],
    tr: ['tr', 'tr-TR'],
    uz: ['uz-Cyrl', 'uz-Cyrl-UZ'], // content is Uzbek in Cyrillic script
    vn: ['vi', 'vi-VN'],
};

/** Markets that have their own pages — the only ones allowed in hreflang / sitemap. */
export const SEO_MARKETS = Object.keys(MARKET_LOCALES);

/** <html lang> for a market. Falls back to English for unknown codes. */
export const getHtmlLang = (isoCountry) =>
    MARKET_LOCALES[String(isoCountry || '').toLowerCase()]?.[0] || 'en';

/** hreflang value for a market, or null when the market has no site of its own. */
export const getHreflang = (isoCountry) =>
    MARKET_LOCALES[String(isoCountry || '').toLowerCase()]?.[1] || null;

export default MARKET_LOCALES;
